import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, DollarSign, Users, FolderOpen, Clock,
  ArrowRight, CheckCircle, AlertCircle, Plus, Award, UserPlus
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { StatCard, Badge, LoadingPage, formatCurrency, formatDate, Alert } from '../components/shared';
import {
  topicsAPI, applicationsAPI, sponsorshipsAPI, projectsAPI, adminAPI
} from '../lib/api';
import api from '../lib/api';
import toast from 'react-hot-toast';

export default function DashboardHome() {
  const { user, isApproved } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboardData(); }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;
    try {
      if (user.role === 'student') {
        const [apps, projects, invitations, entryOptions] = await Promise.all([
          applicationsAPI.getMy(),
          isApproved ? projectsAPI.getAll() : Promise.resolve({ data: [] }),
          isApproved ? api.get('/teams/my-invitations') : Promise.resolve({ data: [] }),
          isApproved ? projectsAPI.getMyEntryOptions() : Promise.resolve({ data: { options: [] } }),
        ]);
        setData({ applications: apps.data, projects: projects.data, invitations: invitations.data, entryOptions: entryOptions.data.options || [] });
      } else if (user.role === 'lecturer') {
        const [topics, projects] = await Promise.all([
          topicsAPI.getMy(),
          isApproved ? projectsAPI.getAll() : Promise.resolve({ data: [] })
        ]);
        setData({ topics: topics.data, projects: projects.data });
      } else if (user.role === 'alumni') {
        const [sponsorships, projects] = await Promise.all([
          sponsorshipsAPI.getMy(),
          isApproved ? projectsAPI.getAll() : Promise.resolve({ data: [] })
        ]);
        setData({ sponsorships: sponsorships.data, projects: projects.data });
      } else if (user.role === 'admin') {
        const dashboard = await adminAPI.getDashboard();
        setData(dashboard.data);
      } else if (user.role === 'judge') {
        const { judgingAPI } = await import('../lib/api');
        const assigned = await judgingAPI.getAssigned();
        setData({ assigned: assigned.data });
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>
          {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2.25rem', fontWeight: 300, lineHeight: 1.1 }}>
          Good {getTimeOfDay()},{' '}
          <span style={{ color: 'var(--gold)' }}>{user.first_name}</span>
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>{getSubtitle(user.role)}</p>
      </div>

      {!isApproved && user.role !== 'admin' && (
        <div style={{ marginBottom: 28 }}>
          <Alert type="warning">
            Your account is pending admin approval. Some features are restricted until approval.
            You'll receive an email when your account is approved.
          </Alert>
        </div>
      )}

      {user.role === 'student' && <StudentDashboard data={data} isApproved={isApproved} onReload={loadDashboardData} />}
      {user.role === 'lecturer' && <LecturerDashboard data={data} isApproved={isApproved} />}
      {user.role === 'alumni' && <AlumniDashboard data={data} isApproved={isApproved} />}
      {user.role === 'admin' && <AdminDashboard data={data} />}
      {user.role === 'judge' && <JudgeDashboard data={data} />}
    </div>
  );
}

// ── Finale Entry Banner ───────────────────────────────────────────────────────
function FinaleEntryBanner({ entryOptions }) {
  if (!entryOptions || entryOptions.length === 0) return null;

  const entered = entryOptions.filter(o => o.entry_deck_path);
  const allEntered = entered.length === entryOptions.length;

  if (allEntered) {
    return (
      <Link to="/dashboard/enter-finale" style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24,
        padding: '14px 20px', borderRadius: 'var(--radius-md)',
        background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
        color: 'var(--success)', textDecoration: 'none', fontSize: '0.875rem',
      }}>
        <CheckCircle size={18} />
        <span>You're entered for the Finale — update your entry</span>
        <ArrowRight size={15} style={{ marginLeft: 'auto' }} />
      </Link>
    );
  }

  return (
    <div className="card" style={{
      marginBottom: 24,
      border: '1px solid rgba(201,168,76,0.4)',
      background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(201,168,76,0.02) 100%)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Award size={20} color="var(--gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', fontWeight: 600, color: 'var(--gold)' }}>
          Enter the Grand Finale
        </h2>
      </div>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
        Upload your entry deck and you're in — no approval needed. One upload, and you're competing on 30 July.
      </p>
      <Link to="/dashboard/enter-finale" className="btn btn-primary">
        Upload Entry Deck & Enter the Finale <ArrowRight size={15} />
      </Link>
    </div>
  );
}

// ── Team Invitations Card ─────────────────────────────────────────────────────
function TeamInvitationsCard({ invitations, onReload }) {
  const [responding, setResponding] = useState({});

  const handleRespond = async (teamId, memberId, action) => {
    setResponding(r => ({ ...r, [teamId]: true }));
    try {
      await api.patch(`/teams/${teamId}/invitations/${memberId}`, { action });
      toast.success(action === 'accepted' ? 'Invitation accepted!' : 'Invitation declined.');
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to respond.');
    } finally {
      setResponding(r => ({ ...r, [teamId]: false }));
    }
  };

  if (!invitations?.length) return null;

  return (
    <div className="card" style={{
      marginBottom: 24,
      border: '1px solid rgba(201,168,76,0.3)',
      background: 'rgba(201,168,76,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <UserPlus size={18} color="var(--gold)" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600, color: 'var(--gold)' }}>
          Pending Team Invitations
        </h2>
        <span style={{ fontSize: '0.72rem', background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', padding: '2px 8px', borderRadius: 999 }}>
          {invitations.length}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {invitations.map(inv => (
          <div key={inv.team_id} style={{
            padding: '14px 16px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: 3 }}>
                {inv.team_name}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--teal)' }}>{inv.lead_name}</span> invited you to join this team for{' '}
                <strong style={{ color: 'var(--text-primary)' }}>{inv.topic_title}</strong>
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Invited {new Date(inv.invited_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-primary btn-sm"
                disabled={responding[inv.team_id]}
                onClick={() => handleRespond(inv.team_id, null, 'accepted')}
              >
                {responding[inv.team_id] ? '…' : '✓ Accept'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={responding[inv.team_id]}
                onClick={() => handleRespond(inv.team_id, null, 'declined')}
                style={{ color: 'var(--error)' }}
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── STUDENT ───────────────────────────────────────────────────────────────────
function StudentDashboard({ data, isApproved, onReload }) {
  const apps = data?.applications || [];
  const projects = data?.projects || [];
  const invitations = data?.invitations || [];
  const entryOptions = data?.entryOptions || [];
  const pending = apps.filter(a => a.status === 'pending').length;
  const accepted = apps.filter(a => a.status === 'accepted').length;

  return (
    <div>
      {/* Finale entry — the first thing a student should see */}
      <FinaleEntryBanner entryOptions={entryOptions} />

      {/* Team invitations — shown prominently if any pending */}
      <TeamInvitationsCard invitations={invitations} onReload={onReload} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard label="Applications" value={apps.length} icon={BookOpen} color="teal" sub={`${pending} pending`} />
        <StatCard label="Accepted" value={accepted} icon={CheckCircle} color="gold" />
        <StatCard label="Active Projects" value={projects.filter(p => p.status === 'in_progress').length} icon={FolderOpen} color="purple" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500 }}>Recent Applications</h2>
            <Link to="/dashboard/applications" className="btn btn-ghost btn-sm">View all <ArrowRight size={14} /></Link>
          </div>
          {apps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
              <BookOpen size={32} style={{ opacity: 0.3, margin: '0 auto 12px' }} />
              <p style={{ fontSize: '0.875rem' }}>No applications yet</p>
              {isApproved && (
                <Link to="/dashboard/marketplace" className="btn btn-secondary btn-sm" style={{ marginTop: 12 }}>Browse Topics</Link>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {apps.slice(0, 4).map(app => (
                <div key={app.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--radius-md)' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.3 }}>{app.topic_title}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{app.department}</div>
                  </div>
                  <Badge status={app.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500, marginBottom: 20 }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {isApproved ? <>
              <Link to="/dashboard/marketplace" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}>
                <BookOpen size={16} /> Browse Research Topics
              </Link>
              <Link to="/dashboard/projects" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}>
                <FolderOpen size={16} /> My Projects
              </Link>
              <Link to="/dashboard/profile" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}>
                <Users size={16} /> Update Your Profile
              </Link>
            </> : (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.7 }}>
                Complete your profile and wait for admin approval to start applying for topics and submitting projects.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── LECTURER ──────────────────────────────────────────────────────────────────
function LecturerDashboard({ data, isApproved }) {
  const topics = data?.topics || [];
  const totalApplicants = topics.reduce((sum, t) => sum + parseInt(t.application_count || 0), 0);
  const sponsored = topics.filter(t => t.is_sponsored).length;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard label="My Topics" value={topics.length} icon={BookOpen} color="teal" sub={`${sponsored} sponsored`} />
        <StatCard label="Total Applicants" value={totalApplicants} icon={Users} color="gold" />
        <StatCard label="Open Topics" value={topics.filter(t => t.status === 'open').length} icon={CheckCircle} color="green" />
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500 }}>My Research Topics</h2>
          <div style={{ display: 'flex', gap: 10 }}>
            {isApproved && (
              <Link to="/dashboard/my-topics?new=1" className="btn btn-primary btn-sm"><Plus size={14} /> New Topic</Link>
            )}
            <Link to="/dashboard/my-topics" className="btn btn-ghost btn-sm">View all <ArrowRight size={14} /></Link>
          </div>
        </div>
        {topics.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
            <BookOpen size={32} style={{ opacity: 0.3, margin: '0 auto 12px' }} />
            <p style={{ fontSize: '0.875rem' }}>No topics submitted yet</p>
            {isApproved && <Link to="/dashboard/my-topics" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>Submit a Topic</Link>}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr><th>Topic</th><th>Status</th><th>Applicants</th><th>Funding</th><th></th></tr>
              </thead>
              <tbody>
                {topics.slice(0, 5).map(t => (
                  <tr key={t.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{t.title}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t.domain}</div>
                    </td>
                    <td><Badge status={t.status} /></td>
                    <td><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--teal)' }}>{t.application_count || 0}</span></td>
                    <td>{t.total_funding > 0 ? <span style={{ color: 'var(--gold)' }}>{formatCurrency(t.total_funding)}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td><Link to={`/dashboard/topics/${t.id}/applications`} className="btn btn-ghost btn-sm">Review</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ALUMNI ────────────────────────────────────────────────────────────────────
function AlumniDashboard({ data, isApproved }) {
  const sponsorships = data?.sponsorships || [];
  const projects = data?.projects || [];
  const totalFunding = sponsorships.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard label="Sponsorships" value={sponsorships.length} icon={DollarSign} color="gold" />
        <StatCard label="Total Pledged" value={formatCurrency(totalFunding)} icon={Award} color="teal" />
        <StatCard label="Active Projects" value={projects.filter(p => p.status === 'in_progress').length} icon={FolderOpen} color="green" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500 }}>My Sponsorships</h2>
            <Link to="/dashboard/sponsorships" className="btn btn-ghost btn-sm">View all <ArrowRight size={14} /></Link>
          </div>
          {sponsorships.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              No sponsorships yet.{' '}
              {isApproved && <Link to="/dashboard/marketplace" style={{ color: 'var(--gold)' }}>Browse topics to sponsor →</Link>}
            </div>
          ) : sponsorships.slice(0, 4).map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{s.topic_title}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.applicant_count} applicants</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: 'var(--gold)', fontFamily: 'var(--font-mono)', fontSize: '0.875rem' }}>{formatCurrency(s.amount)}</div>
                <Badge status={s.status} />
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500, marginBottom: 20 }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {isApproved && <>
              <Link to="/dashboard/marketplace" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}>
                <BookOpen size={16} /> Browse Research Topics
              </Link>
              <Link to="/dashboard/sponsorships" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}>
                <DollarSign size={16} /> My Sponsorships
              </Link>
              <Link to="/dashboard/profile" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}>
                <Users size={16} /> Update Profile
              </Link>
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ADMIN ─────────────────────────────────────────────────────────────────────
function AdminDashboard({ data }) {
  if (!data) return null;
  const { users, topics, applications, projects, funding } = data;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard label="Total Users" value={users?.total} icon={Users} color="teal" sub={`${users?.pending} pending`} />
        <StatCard label="Topics" value={topics?.total} icon={BookOpen} color="gold" sub={`${topics?.pending_approval} awaiting approval`} />
        <StatCard label="Applications" value={applications?.total} icon={FolderOpen} color="blue" sub={`${applications?.accepted} accepted`} />
        <StatCard label="Projects" value={projects?.total} icon={Award} color="green" sub={`${projects?.in_progress} in progress`} />
        <StatCard label="Total Funding" value={formatCurrency(funding?.total_pledged)} icon={DollarSign} color="purple" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div className="card card-gold">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500, marginBottom: 16 }}>Pending Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {users?.pending > 0 && (
              <Link to="/dashboard/users?status=pending" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius-md)', color: '#f59e0b' }}>
                <span>{users.pending} users awaiting approval</span><ArrowRight size={16} />
              </Link>
            )}
            {topics?.pending_approval > 0 && (
              <Link to="/dashboard/topics?status=pending_approval" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 14px', background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.2)', borderRadius: 'var(--radius-md)', color: 'var(--gold)' }}>
                <span>{topics.pending_approval} topics awaiting approval</span><ArrowRight size={16} />
              </Link>
            )}
            {users?.pending === 0 && topics?.pending_approval === 0 && (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', padding: '12px 0' }}>✓ No pending actions</p>
            )}
          </div>
        </div>

        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500, marginBottom: 16 }}>Quick Links</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Link to="/dashboard/users" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}><Users size={16} /> Manage Users</Link>
            <Link to="/dashboard/analytics" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}><Award size={16} /> View Analytics</Link>
            <Link to="/dashboard/config" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12 }}><Clock size={16} /> Program Configuration</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── JUDGE ─────────────────────────────────────────────────────────────────────
function JudgeDashboard({ data }) {
  const assigned = data?.assigned || [];
  const scored = assigned.filter(p => p.score_id).length;
  const remaining = assigned.length - scored;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard label="Assigned Projects" value={assigned.length} icon={FolderOpen} color="teal" />
        <StatCard label="Scored" value={scored} icon={CheckCircle} color="gold" />
        <StatCard label="Remaining" value={remaining} icon={Clock} color={remaining > 0 ? 'red' : 'green'} />
      </div>

      <div className="card">
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 500, marginBottom: 20 }}>Assigned Projects</h2>
        {assigned.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No projects assigned yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {assigned.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{p.topic_title || p.title}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Student: {p.student_name} · Domain: {p.domain}</div>
                </div>
                <Link to={`/dashboard/judging/${p.id}`} className={`btn btn-sm ${p.score_id ? 'btn-ghost' : 'btn-primary'}`}>
                  {p.score_id ? 'View Score' : 'Score Now'}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function getSubtitle(role) {
  return {
    student: 'Browse research topics, apply, and track your project progress.',
    lecturer: 'Manage your research topics and review student applications.',
    alumni: 'Fund innovation. Browse topics and select outstanding students.',
    admin: 'Platform overview — users, topics, applications and program configuration.',
    judge: 'Review assigned projects and submit your evaluations.',
  }[role] || 'Welcome to the Futurum platform.';
}
